import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/App";
import { Search, TrendingUp, TrendingDown, Filter, ArrowRight, Loader2 } from "lucide-react";

export const FundResearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("");
  const [funds, setFunds] = useState([]);
  const [topFunds, setTopFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchTopFunds();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/funds/categories/list`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTopFunds = async () => {
    try {
      const response = await axios.get(`${API}/funds/top-performers`);
      setTopFunds(response.data.funds || []);
    } catch (error) {
      console.error("Error fetching top funds:", error);
    }
  };

  const searchFunds = async () => {
    if (!searchQuery && !category) {
      setFunds([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/funds/search`, {
        params: { q: searchQuery, category }
      });
      setFunds(response.data.funds || []);
    } catch (error) {
      console.error("Error searching funds:", error);
      setFunds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2 || category) {
        searchFunds();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, category]);

  // Sample fund data for demo
  const sampleFunds = [
    { schemeCode: "119551", schemeName: "Aditya Birla Sun Life Frontline Equity Fund", returns1Y: 24.5, returns3Y: 18.2, category: "Large Cap", risk: "Moderate" },
    { schemeCode: "120503", schemeName: "HDFC Mid-Cap Opportunities Fund", returns1Y: 32.1, returns3Y: 22.5, category: "Mid Cap", risk: "High" },
    { schemeCode: "118989", schemeName: "Axis Bluechip Fund", returns1Y: 20.3, returns3Y: 16.8, category: "Large Cap", risk: "Moderate" },
    { schemeCode: "125354", schemeName: "Mirae Asset Large Cap Fund", returns1Y: 22.7, returns3Y: 17.9, category: "Large Cap", risk: "Moderate" },
    { schemeCode: "100356", schemeName: "ICICI Prudential Bluechip Fund", returns1Y: 21.4, returns3Y: 17.2, category: "Large Cap", risk: "Moderate" },
    { schemeCode: "120505", schemeName: "SBI Small Cap Fund", returns1Y: 38.5, returns3Y: 28.1, category: "Small Cap", risk: "Very High" },
    { schemeCode: "120847", schemeName: "Parag Parikh Flexi Cap Fund", returns1Y: 26.8, returns3Y: 21.3, category: "Flexi Cap", risk: "High" },
    { schemeCode: "119598", schemeName: "Kotak Emerging Equity Fund", returns1Y: 34.2, returns3Y: 24.7, category: "Mid Cap", risk: "High" },
    { schemeCode: "118834", schemeName: "HDFC Balanced Advantage Fund", returns1Y: 18.5, returns3Y: 14.2, category: "Hybrid", risk: "Moderate" },
    { schemeCode: "119597", schemeName: "ICICI Prudential Equity & Debt Fund", returns1Y: 19.8, returns3Y: 15.6, category: "Hybrid", risk: "Moderate" },
  ];

  const displayFunds = funds.length > 0 ? funds : (searchQuery || category ? [] : sampleFunds);

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case "low": return "bg-green-500/10 text-green-500";
      case "moderate": return "bg-yellow-500/10 text-yellow-500";
      case "high": return "bg-orange-500/10 text-orange-500";
      case "very high": return "bg-red-500/10 text-red-500";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="fund-research-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Fund <span className="gold-text">Research</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Explore 37,000+ mutual funds from 40+ AMCs with real-time NAV data from AMFI
            </p>
          </div>

          {/* Search Section */}
          <Card className="glass-card mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search funds by name or code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-secondary/50 border-white/10"
                    data-testid="fund-search-input"
                  />
                </div>
                <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-full md:w-48 bg-secondary/50 border-white/10" data-testid="category-select">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={searchFunds} 
                  className="btn-primary"
                  disabled={loading}
                  data-testid="search-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Fund Categories Quick Links */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={category === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(category === cat.id ? "" : cat.id)}
                className={category === cat.id ? "btn-primary" : "border-white/10"}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-4">Searching funds...</p>
            </div>
          )}

          {/* Fund Results */}
          {!loading && (
            <div className="space-y-4">
              {displayFunds.length > 0 ? (
                displayFunds.map((fund, index) => (
                  <Link key={fund.schemeCode || index} to={`/research/${fund.schemeCode}`}>
                    <Card className="glass-card card-hover cursor-pointer" data-testid={`fund-card-${index}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <span className="text-primary font-bold text-sm">
                                  {fund.schemeName?.charAt(0) || "F"}
                                </span>
                              </div>
                              <div>
                                <h3 className="font-medium mb-1">{fund.schemeName}</h3>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {fund.category || "Equity"}
                                  </Badge>
                                  {fund.risk && (
                                    <Badge className={`text-xs ${getRiskColor(fund.risk)}`}>
                                      {fund.risk} Risk
                                    </Badge>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    Code: {fund.schemeCode}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            {fund.returns1Y && (
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground mb-1">1Y Returns</p>
                                <div className={`flex items-center gap-1 font-mono font-semibold ${
                                  fund.returns1Y >= 0 ? "text-green-500" : "text-red-500"
                                }`}>
                                  {fund.returns1Y >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                  {fund.returns1Y >= 0 ? "+" : ""}{fund.returns1Y}%
                                </div>
                              </div>
                            )}
                            {fund.returns3Y && (
                              <div className="text-center hidden sm:block">
                                <p className="text-xs text-muted-foreground mb-1">3Y Returns</p>
                                <div className={`flex items-center gap-1 font-mono font-semibold ${
                                  fund.returns3Y >= 0 ? "text-green-500" : "text-red-500"
                                }`}>
                                  {fund.returns3Y >= 0 ? "+" : ""}{fund.returns3Y}%
                                </div>
                              </div>
                            )}
                            <ArrowRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))
              ) : (searchQuery || category) ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No funds found. Try a different search term.</p>
                </div>
              ) : null}
            </div>
          )}

          {/* Top Performers Section */}
          {!searchQuery && !category && topFunds.length > 0 && (
            <div className="mt-12">
              <h2 className="font-serif text-2xl font-bold mb-6">Top Performing Funds</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {topFunds.slice(0, 6).map((fund, index) => (
                  <Link key={fund.schemeCode || index} to={`/research/${fund.schemeCode}`}>
                    <Card className="glass-card card-hover h-full" data-testid={`top-fund-${index}`}>
                      <CardContent className="p-6">
                        <h3 className="font-medium text-sm line-clamp-2 mb-3">{fund.schemeName}</h3>
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-xs">{fund.category}</Badge>
                          <div className="flex items-center gap-1 text-green-500 font-mono">
                            <TrendingUp className="w-4 h-4" />
                            +{fund.returns1Y}%
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
